import React, { useEffect, useState } from 'react';
import { usersAPI } from '../services/api';
import {
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface User {
  id: number;
  username: string;
  email: string | null;
  is_admin: boolean;
  is_active: boolean;
  monthly_cost_limit: number | null;
  current_month_cost: number;
  must_reset_password: boolean;
  invited_at: string | null;
  created_at: string;
}

interface InviteUserForm {
  email: string;
  monthly_cost_limit: string;
  is_admin: boolean;
}

interface EditUserForm {
  monthly_cost_limit: string;
  is_active: boolean;
  password: string;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [inviteForm, setInviteForm] = useState<InviteUserForm>({
    email: '',
    monthly_cost_limit: '5.00',
    is_admin: false,
  });
  const [editForm, setEditForm] = useState<EditUserForm>({
    monthly_cost_limit: '',
    is_active: true,
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.list();
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setInviting(true);
    try {
      const limit = inviteForm.monthly_cost_limit
        ? parseFloat(inviteForm.monthly_cost_limit)
        : undefined;
      await usersAPI.invite({
        email: inviteForm.email,
        monthly_cost_limit: limit,
        is_admin: inviteForm.is_admin,
      });
      setShowInviteModal(false);
      setInviteForm({
        email: '',
        monthly_cost_limit: '5.00',
        is_admin: false,
      });
      setSuccess(`Invitation sent to ${inviteForm.email}`);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setError(null);
    try {
      const updateData: { monthly_cost_limit?: number; is_active?: boolean; password?: string } = {};

      if (editForm.monthly_cost_limit !== '') {
        updateData.monthly_cost_limit = parseFloat(editForm.monthly_cost_limit);
      }
      updateData.is_active = editForm.is_active;
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      await usersAPI.update(editingUser.id, updateData);
      setEditingUser(null);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Are you sure you want to delete user "${user.username}"? This will delete all their chats and data.`)) {
      return;
    }
    try {
      await usersAPI.delete(user.id);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditForm({
      monthly_cost_limit: user.monthly_cost_limit?.toString() || '',
      is_active: user.is_active,
      password: '',
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-surface-100">User Management</h1>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <EnvelopeIcon className="w-5 h-5" />
            Invite User
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
            {success}
          </div>
        )}

        {/* Users table */}
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-700">
                <th className="text-left p-4 text-surface-400 font-medium">User</th>
                <th className="text-left p-4 text-surface-400 font-medium">Status</th>
                <th className="text-left p-4 text-surface-400 font-medium">Monthly Limit</th>
                <th className="text-left p-4 text-surface-400 font-medium">Current Cost</th>
                <th className="text-right p-4 text-surface-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-surface-800 last:border-0">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                        {user.is_admin ? (
                          <ShieldCheckIcon className="w-5 h-5 text-yellow-400" />
                        ) : user.must_reset_password ? (
                          <ClockIcon className="w-5 h-5 text-amber-400" />
                        ) : (
                          <UserCircleIcon className="w-5 h-5 text-surface-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-surface-100 font-medium">
                          {user.email || user.username}
                        </div>
                        {user.is_admin && (
                          <div className="text-xs text-yellow-400">Admin</div>
                        )}
                        {user.must_reset_password && !user.is_admin && (
                          <div className="text-xs text-amber-400">Pending invitation</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        user.must_reset_password
                          ? 'bg-amber-500/20 text-amber-400'
                          : user.is_active
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {user.must_reset_password ? 'Invited' : user.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="p-4 text-surface-300">
                    {user.monthly_cost_limit !== null
                      ? `$${user.monthly_cost_limit.toFixed(2)}`
                      : 'Unlimited'}
                  </td>
                  <td className="p-4">
                    <span
                      className={`${
                        user.monthly_cost_limit !== null &&
                        user.current_month_cost >= user.monthly_cost_limit
                          ? 'text-red-400'
                          : 'text-surface-300'
                      }`}
                    >
                      ${user.current_month_cost.toFixed(2)}
                    </span>
                    {user.monthly_cost_limit !== null && (
                      <div className="w-24 h-1 bg-surface-700 rounded-full mt-1">
                        <div
                          className={`h-full rounded-full ${
                            user.current_month_cost >= user.monthly_cost_limit
                              ? 'bg-red-500'
                              : user.current_month_cost >= user.monthly_cost_limit * 0.8
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (user.current_month_cost / user.monthly_cost_limit) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    {!user.is_admin && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                          title="Edit user"
                        >
                          <PencilIcon className="w-4 h-4 text-surface-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete user"
                        >
                          <TrashIcon className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-surface-100 mb-2">Invite New User</h2>
              <p className="text-surface-400 text-sm mb-4">
                Send an invitation email with login credentials.
              </p>
              <form onSubmit={handleInviteUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, email: e.target.value })
                    }
                    className="input-field"
                    required
                    placeholder="user@example.com"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Monthly Cost Limit ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inviteForm.monthly_cost_limit}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, monthly_cost_limit: e.target.value })
                    }
                    className="input-field"
                    placeholder="Leave empty for unlimited"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_admin"
                    checked={inviteForm.is_admin}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, is_admin: e.target.checked })
                    }
                    className="rounded border-surface-600 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="is_admin" className="text-sm text-surface-300">
                    Admin privileges
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="btn-secondary"
                    disabled={inviting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={inviting}>
                    {inviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-surface-100 mb-4">
                Edit User: {editingUser.username}
              </h2>
              <form onSubmit={handleEditUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Monthly Cost Limit ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.monthly_cost_limit}
                    onChange={(e) =>
                      setEditForm({ ...editForm, monthly_cost_limit: e.target.value })
                    }
                    className="input-field"
                    placeholder="Leave empty to keep unchanged"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    New Password (optional)
                  </label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) =>
                      setEditForm({ ...editForm, password: e.target.value })
                    }
                    className="input-field"
                    placeholder="Leave empty to keep current"
                    minLength={6}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editForm.is_active}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.checked })
                    }
                    className="rounded border-surface-600 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-surface-300">
                    Account active
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
